import {
  Box,
  Card,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MissionControlTenant } from "src/store/api/generatedApi";
import { useMissionControlPlannedShredRanking } from "./useMissionControlPlannedShredRanking";
import { PlannedShredChart } from "./PlannedShredChart";

const PRODUCT_GROUP_ORDER = ["REBAR", "MBQ", "SBQ"];

const pgOrder = (name: string) => {
  const i = PRODUCT_GROUP_ORDER.indexOf(name);
  return i === -1 ? PRODUCT_GROUP_ORDER.length : i;
};

export const PlannedShredPanel = ({
  tenants,
}: {
  tenants: MissionControlTenant[];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    isLoading,
    productGroupRankings,
    availableMonths,
    selectedMonthIndex,
    setSelectedMonthIndex,
  } = useMissionControlPlannedShredRanking(tenants);

  if (isLoading) {
    return (
      <Card
        sx={{
          px: 2,
          py: 1.5,
          background: "rgba(255,255,255,0.35)",
          backdropFilter: "saturate(180%) blur(18px)",
          WebkitBackdropFilter: "saturate(180%) blur(18px)",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ mb: 0.75 }}>
          <Skeleton variant="text" width={200} height={20} />
          <Stack direction="row" alignItems="center" sx={{ ml: "auto" }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={64} height={16} sx={{ mx: 0.5 }} />
            <Skeleton variant="circular" width={20} height={20} />
          </Stack>
        </Stack>
        <Stack direction="row" gap={3}>
          {[0, 1].map((col) => (
            <Box key={col} sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                width={60}
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "max-content max-content 1fr",
                  columnGap: 0.25,
                  rowGap: 0.25,
                  alignItems: "center",
                }}
              >
                {Array.from({ length: 5 }, (_, i) => [
                  <Skeleton
                    key={`n${i}`}
                    variant="text"
                    width={32}
                    height={16}
                  />,
                  <Skeleton
                    key={`p${i}`}
                    variant="text"
                    width={36}
                    height={16}
                  />,
                  <Skeleton
                    key={`b${i}`}
                    variant="rounded"
                    height={10}
                    sx={{ width: `${80 - i * 12}%`, borderRadius: 0.5 }}
                  />,
                ])}
              </Box>
            </Box>
          ))}
        </Stack>
      </Card>
    );
  }

  if (productGroupRankings.length === 0) return null;

  const selectedMonth = availableMonths[selectedMonthIndex];

  return (
    <Card
      sx={{
        px: 2,
        py: 1.5,
        background: "rgba(255,255,255,0.35)",
        backdropFilter: "saturate(180%) blur(18px)",
        WebkitBackdropFilter: "saturate(180%) blur(18px)",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ color: theme.palette.text.primary }}
        >
          {t("topPlannedShredConsumers")}
        </Typography>
        <Stack direction="row" alignItems="center" sx={{ ml: "auto" }}>
          <IconButton
            size="small"
            disabled={selectedMonthIndex >= availableMonths.length - 1}
            onClick={() => {
              setSelectedMonthIndex(
                Math.min(selectedMonthIndex + 1, availableMonths.length - 1)
              );
            }}
            sx={{ p: 0.25 }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", minWidth: 64 }}
          >
            {selectedMonth ? dayjs(selectedMonth).format("MMM YYYY") : ""}
          </Typography>
          <IconButton
            size="small"
            disabled={selectedMonthIndex <= 0}
            onClick={() => {
              setSelectedMonthIndex(Math.max(selectedMonthIndex - 1, 0));
            }}
            sx={{ p: 0.25 }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction="row" gap={3}>
        {[...productGroupRankings]
          .sort(
            (a, b) => pgOrder(a.productGroupName) - pgOrder(b.productGroupName)
          )
          .map((ranking) => (
            <PlannedShredChart
              key={ranking.productGroupName}
              productGroupName={ranking.productGroupName}
              tenants={ranking.tenants}
              color={theme.palette.data.blue}
            />
          ))}
      </Stack>
    </Card>
  );
};
